import {
	findRaisedRegEdRound,
	holdsOpenInternalRound,
	IRegEdSubmissionInput,
	IRegEdSubmissionView,
	RegEdComplianceContentType,
	useRegEdRevisionFiling,
	useRegEdSocialPostRegenerateCompliancePdfMutation,
	useRegEdSocialPostReviseMutation,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { IUser, PostStatus, UserViewModel } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { Switch, useLocation } from 'react-router';
import { toast } from 'sonner';
import { AccountUserSelectBox } from '../../../../admin/components/user/AccountUserSelectBox';
import {
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
	SocialMediaPostStatusDetails,
} from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { EDuplicateType, suppressDuplicateTemplateWarning } from '../../../../models/Campaigns';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import {
	accountHasComplianceEnabled,
	createContentStateWithHtmlStringValue,
	getSocialMediaPlainTextContentFromRawRichContent,
	getStatusGroupOptions,
	isValidEmail,
} from '../../../../models/UiUtils';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	findCustomizationMarkersInPlainText,
	ICustomizationMarker,
} from '../../../../models/utils/templateCustomizationUtils';
import { shouldShowSendOnBehalfUI } from '../../../../models/permissions/sendOnBehalf';
import {
	invalidateAllTemplateCategorySocialQueries,
	useCreateSocialPostMutation,
	useUpdateTemplateMutation,
} from '../../../../queries';
import { useUsersWithSocialMediaConnection } from '../../../../queries/User/useUsersWithSocialMediaConnection';
import { KnownCategories } from '../../../../viewmodels/AppViewModels';
import { THEME } from '../../../components-v2/Theme/theme';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { CustomizationRequiredDialog } from '../../../components/CustomizationRequiredDialog';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Modal } from '../../../components/Modal';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { SaveTemplate } from '../../../components/SaveTemplate';
import { getTemplateApprovalStatus } from '../../../components/TemplateReviewStatusIndicator';
import { TextInputModal } from '../../../components/TextInputModal';
import { PopoverType, TinyPopover } from '../../../components/TinyPopover';
import { UserSelectBox } from '../../../components/UserSelectBox';
import { lastUsedComplianceRecipientEmail } from '../../../components/campaigns/CampaignDetailsSummary';
import { ComplianceConfirmation } from '../../../components/campaigns/ComplianceConfirmation';
import { DuplicateWarningDialog } from '../../../components/campaigns/DuplicateWarningDialog';
import { InlineValueEditor } from '../../../components/entities/InlineValueEditor';
import { SocialMediaPlatformTable } from '../../../components/socialMedia/SocialMediaPlatformTable';
import { SocialMediaPlatformTableType } from '../../../components/socialMedia/SocialMediaPlatformTable/models';
import { IPlatformValidationError, getSaveBlockingErrors } from '../../../../models/utils/socialPostValidation';
import {
	findPlatformCustomization,
	getPublishedContent,
	getTargetedPlatforms,
	hasPublishedContent,
	toApiPlatformCustomizations,
} from '../../../../models/utils/platformCustomizations';
import { usePlatformCustomization } from '../../../components/socialMedia/PlatformCustomization/usePlatformCustomization';
import { SchedulePreflightDialog } from '../../../components/socialMedia/SchedulePreflightDialog';
import { SocialMediaPostContentEditor } from '../../../components/socialMedia/SocialMediaPostContentEditor';
import {
	getPostBrandingVersions,
	useBrandPostMedia,
} from '../../../components/socialMedia/SocialMediaPostContentEditor/useBrandPostMedia';
import { useBrandingTabIndicator } from '../../../components/socialMedia/SocialMediaPostContentEditor/useBrandingTabIndicator';
import { SocialMediaPostGraphic } from '../../../components/svgs/graphics/SocialMediaPostGraphic';
import { EditItemPenIcon } from '../../../components/svgs/icons/EditItemPenIcon';
import { InfoIcon } from '../../../components/svgs/icons/InfoIcon';
import { LightBulbIcon } from '../../../components/svgs/icons/LightBulbIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { ViewCitationsModal } from '../../../components/templates/ViewCitationsModal';
import { brandPrimaryHover } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { AIScheduleSuccess } from '../../shared/AIScheduleSuccess';
import { CampaignSessionManager, SessionProgressUpdate } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import { useComposerAISource } from '../../shared/AIScheduleSuccess/useComposerAISource';
import { SocialMediaApprovalSetSchedule } from '../../socialMediaPostApproval/SocialMediaApprovalSetSchedule';
import { UniversalAssistantChatWithinComposer } from '../../templates/AssistantChat';
import { EditSocialMediaSendOptions } from '../../templates/EditSocialMediaSendOptions';
import { CopyPreviewLink } from '../CopyPreviewLink';
import { SocialMediaPostScheduleComplete } from '../SocialMediaPostScheduleComplete';
import { EditSocialMediaPostContext } from './context';
import { useEditSocialMediaPost } from './hooks';
import { MobileEditSocialMediaPostContainer } from './mobile';
import { CancelConfirmationOptions, CancelUpdateConfirmationOptions, UpdateConfirmationOptions } from './models';
import { UnsavedChangesWarning } from '../../../components-v2/UnsavedChangesWarning';
import { DefaultComposerContext, SocialMediaNavigation, StatusSpecificDetails } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps, IModalContext {
	className?: string;
	onPostUpdated?(): boolean;
	styles?: StyleDeclarationValue[];
}

const EditSocialMediaPostBase: React.FC<IProps> = observer(props => {
	const { className, styles = [], impersonationContext, parentModal, onPostUpdated } = props;

	const {
		context,
		duplicateType,
		errorMessages,
		h,
		impersonationCtx,
		initialSelectedUserOptionRef,
		isLoaded,
		isTestimonialMode,
		locationStateCreateRequest,
		logApiError,
		postReport,
		postToEdit,
		routeMatch,
		scheduledPost,
		setScheduledPost,
		setShowDuplicateWarning,
		setSocialMediaConnections,
		showDuplicateWarning,
		socialMediaConnections,
		officerFilesRegEd,
		officerReviewerMissing,
		source,
		regEdProbeLoading,
		submitRegEdCompliance,
		suggestionId,
		templateId,
		usesRegEd,
		userIsLoaded,
	} = useEditSocialMediaPost({ impersonationContext });
	const platformCustomization = usePlatformCustomization({ context, impersonationContext: impersonationCtx });
	const brandingImpersonationContext = React.useMemo(
		() => Api.ImpersonationBroker.withForUser(impersonationCtx, context.selectedCreator?.id),
		[impersonationCtx, context.selectedCreator?.id]
	);
	const branding = useBrandPostMedia({
		impersonationContext: brandingImpersonationContext,
		replaceMedia: (replacement, forPlatform) => context.setActiveImages(current => replacement(current), forPlatform),
		templateId: context.template?.id,
		versions: getPostBrandingVersions(context),
	});
	useBrandingTabIndicator(branding.tabActivity);
	const location = useLocation();
	const { logEvent } = useEventLogging('EditSocialMediaPost');
	const { postNotification } = useLocalNotificationService<Api.SocialMediaPostViewModel>();
	const userSession = useUserSession();
	const usersWithSocial = useUsersWithSocialMediaConnection({
		transformer: users => users.map(user => new UserViewModel(userSession, user)),
		userSession,
	});
	const fullscreenModal = useFullscreenModal();

	const handleCampaignSessionNavigation = React.useCallback(
		(progress?: SessionProgressUpdate | null, socialPost?: Api.SocialMediaPostViewModel) => {
			if (progress?.isCampaignComplete) {
				CampaignSessionManager.clearSession();
				fullscreenModal.dismissModal();
				return;
			}

			const sessionId = progress?.session?.id ?? CampaignSessionManager.getSession()?.id;
			const locationState = sessionId ? { socialPost, sessionId } : { socialPost };

			const pathname = `${routeMatch.url}/schedule-complete`;
			fullscreenModal.history.replace({
				pathname,
				state: locationState,
			});
		},
		[fullscreenModal, routeMatch.url]
	);

	const { showingAIAssistant, setShowingAIAssistant, sourceContent, sourceType, initialGeneration } =
		useComposerAISource('social');

	// Splitting the post disables the assistant, and the drawer may already be open when it happens:
	// its content goes to the base body, which no platform publishes once versions exist.
	React.useEffect(() => {
		if (platformCustomization.isSplit) {
			setShowingAIAssistant(false);
		}
	}, [platformCustomization.isSplit, setShowingAIAssistant]);

	const [showSaveAsModal, setShowSaveAsModal] = React.useState(false);
	const [templateToBeSaved, setTemplateToBeSaved] = React.useState<Api.ITemplate | null>(null);

	const readOnly = source ? !source.canEdit : false;

	const [isSchedulingInProgress, setIsSchedulingInProgress] = React.useState(false);

	const [bypassCompliance, setBypassCompliance] = React.useState(false);
	const [editableCampaignName, setEditableCampaignName] = React.useState<string>(null);
	const [isEditingName, setIsEditingName] = React.useState(false);

	const isComplianceFlow = accountHasComplianceEnabled(userSession, impersonationContext);

	const [sendWithComplianceEmail, setSendWithComplianceEmail] = React.useState<string>(() => {
		return localStorage.getItem(lastUsedComplianceRecipientEmail) ?? '';
	});

	// Captured from the RegEd prompt at "Schedule for Compliance Review" and carried through the
	// send-options step so the 2-step submission can fire once the post (and its AR) exists.
	const [regEdMetadata, setRegEdMetadata] = React.useState<IRegEdSubmissionInput | null>(
		locationStateCreateRequest?.regEdRevise
			? {
					format: locationStateCreateRequest.regEdRevise.format ?? '',
					answers: locationStateCreateRequest.regEdRevise.answers ?? {},
				}
			: null
	);

	const previewLink = source?.anonymousAccessLink || scheduledPost?.anonymousAccessLink || '';

	const onSelectedConnectionsChanged = React.useCallback(
		(selected: Api.IPostTarget[]) => {
			context.setPostTargets(selected);
			context.setHasChanges(true);
		},
		[context]
	);

	// Match on identity rather than reference: the connections list can be re-seeded while the prune is
	// in flight, and a stale reference would leave the pruned row on screen with nothing to explain it.
	const onOrphanedTargetRemoved = React.useCallback(
		(connection: Api.ISocialMediaConnection) => {
			setSocialMediaConnections(current =>
				(current ?? []).filter(
					x =>
						!(
							x?.type === connection?.type &&
							x?.postTargetId === connection?.postTargetId &&
							x?.userId === connection?.userId
						)
				)
			);
		},
		[setSocialMediaConnections]
	);

	const onOverlayLogoChange = React.useCallback(async () => {
		if (!impersonationCtx || !readOnly) {
			return;
		}

		const postToUpdate = scheduledPost || postToEdit;

		if (!postToUpdate) {
			return;
		}

		try {
			const [, error] = await context.updatePost(postToUpdate);

			if (error) {
				logApiError('OverlayLogoAutoSave-Error', error);
				errorMessages.pushApiError(error);
				toast.error('Failed to save overlay logo change.');
				return;
			}

			context.setHasChanges(false);
			toast.success('Overlay logo saved.');
		} catch (err) {
			logApiError('OverlayLogoAutoSave-Error', err);
			errorMessages.pushApiError(err);
			toast.error('Failed to save overlay logo change.');
		}
	}, [context, errorMessages, impersonationCtx, logApiError, postToEdit, readOnly, scheduledPost]);

	const reloadSource = React.useCallback(async () => {
		try {
			await source.impersonate(impersonationCtx)?.load();
		} catch (err) {
			logApiError('ReloadPostReport-Error', err);
			toast.error('Report failed to reload. Please refresh the page to see the latest status.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [source]);

	// Held rather than read straight from the hook so the dialog keeps listing what was wrong at the
	// moment of the confirm, even as the user starts fixing it behind the dialog.
	const [preflightErrors, setPreflightErrors] = React.useState<IPlatformValidationError[]>([]);
	const [preflightAction, setPreflightAction] = React.useState<'save' | 'schedule'>('schedule');

	const [customizationMarkers, setCustomizationMarkers] = React.useState<ICustomizationMarker[]>(undefined);
	const [acknowledgedCustomizationContent, setAcknowledgedCustomizationContent] = React.useState<string>(undefined);

	// The copy that goes out: each targeted version once the post is split, since the base then goes nowhere.
	const getPublishedCopy = () => getPublishedContent(context).join('\n\n');

	/**
	 * Returns false (and opens the advisory dialog) the first time uncustomized template copy is found.
	 * Keyed on the acknowledged copy rather than a boolean, so any edit re-arms the safeguard.
	 */
	const canProceedPastCustomizationCheck = () => {
		const publishedCopy = getPublishedCopy();
		if (
			readOnly ||
			(acknowledgedCustomizationContent !== undefined && acknowledgedCustomizationContent === publishedCopy)
		) {
			return true;
		}

		const markers = findCustomizationMarkersInPlainText(publishedCopy);
		if (markers.length === 0) {
			return true;
		}

		logEvent('CustomizationRequiredWarningShown', {
			contentKind: 'post',
			markerCount: markers.length,
			markerKinds: Array.from(new Set(markers.map(x => x.kind))),
			templateId: context.template?.id,
		});
		setCustomizationMarkers(markers);
		return false;
	};

	const onCustomizationRequiredDialogRequestClose = () => {
		setAcknowledgedCustomizationContent(getPublishedCopy());
		setCustomizationMarkers(undefined);
	};

	const handlePostNow = (e?: React.MouseEvent<HTMLElement>) => {
		if (!canProceedPastCustomizationCheck()) {
			e?.preventDefault();
			return;
		}
		// RegEd has to bypass explicitly, or the server builds an approval request the filing never
		// reaches and the post holds forever. Internal still routes Post Now through compliance.
		onSubmitPost(e, moment(), usesRegEd);
	};

	const handleScheduleWithoutCompliance = (e?: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		if (!canProceedPastCustomizationCheck()) {
			return;
		}
		setBypassCompliance(true);
		// Otherwise a questionnaire filled earlier still files, reports a failure, and holds the send
		// date to the filing's lead-time floor.
		setRegEdMetadata(null);
		h.push(`${routeMatch.url}/send-options`);
	};

	const checkComplianceEmail = () => {
		if (sendWithComplianceEmail.length > 0 && !isValidEmail(sendWithComplianceEmail)) {
			toast.error(`Please enter a valid email address`);
			return false;
		}
		return true;
	};

	const handleScheduleForLater = (e?: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		if (isComplianceFlow && !checkComplianceEmail()) {
			return;
		}
		if (!canProceedPastCustomizationCheck()) {
			return;
		}
		// Undo an earlier decline, or the submit guard drops this filing silently.
		setBypassCompliance(false);
		h.push(`${routeMatch.url}/send-options`);
	};

	const onScheduleRegEdCompliance = (e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionInput) => {
		setRegEdMetadata(metadata);
		handleScheduleForLater(e);
	};

	const handleChangeDate = async () => {
		if (postReport) {
			await reloadSource();
		}
		h.push(`${routeMatch.url}/set-social-schedule`);
	};

	const onCancelPost = React.useCallback(async () => {
		if (scheduledPost) {
			try {
				await scheduledPost.cancel();
				postNotification({
					info: scheduledPost,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				CampaignSessionManager.clearSession();
				parentModal.onRequestClose();
			} catch (err) {
				errorMessages.pushApiError(err);
				logApiError('CancelPost-Error', err);
			}
		}
	}, [scheduledPost, logApiError, errorMessages, parentModal, postNotification]);

	const [showingCancelConfirmation, setShowingCancelConfirmation] = React.useState(false);
	const [showingCancelUpdateConfirmation, setShowingCancelUpdateConfirmation] = React.useState(false);
	const [showingUpdateConfirmation, setShowingUpdateConfirmation] = React.useState(false);
	const [showingCitations, setShowingCitations] = React.useState(false);

	const onShowCancelConfirmation = (e?: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();

		setShowingCancelConfirmation(true);
	};

	const onShowCancelUpdateConfirmation = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowingCancelUpdateConfirmation(true);
	};

	const onRequestClose = () => {
		CampaignSessionManager.clearSession();
		parentModal?.onRequestClose();
	};

	const onShowUpdateConfirmation = () => {
		setShowingUpdateConfirmation(true);
	};

	const onCancelConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			onCancelPost();
		}
		setShowingCancelConfirmation(false);
	};

	const onCancelUpdateConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			CampaignSessionManager.clearSession();
			parentModal?.onRequestClose();
		}
		setShowingCancelUpdateConfirmation(false);
	};

	const onCitationRequestClose = () => {
		setShowingCitations(false);
	};

	const onUpdateConfirmationRequestClose = async (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			const selectedSendTime =
				postToEdit?.status === Api.PostStatus.Draft || scheduledPost?.status === Api.PostStatus.Draft
					? undefined
					: moment(postReport?.dueDate || postToEdit?.dueDate);
			await onSubmitPost(undefined, selectedSendTime);
			postNotification({
				info: scheduledPost,
				topic: Topics.EDIT_SOCIAL_POST,
			});
		}
		setShowingUpdateConfirmation(false);
	};

	// A new draft goes straight to create rather than through onSubmitPost, so it answers to the same
	// save rules here, before the user is asked to name something that cannot be stored.
	const onSaveAsButtonClicked = () => {
		const blockers = getSaveBlockingErrors(platformCustomization.errors);
		if (blockers.length) {
			setPreflightAction('save');
			setPreflightErrors(blockers);
			return;
		}
		setShowSaveAsModal(true);
	};

	const createSocialPostMutation = useCreateSocialPostMutation({
		onSuccess: async data => {
			const socialMediaPostVm = new Api.SocialMediaPostViewModel(userSession, data);
			postNotification({
				info: socialMediaPostVm,
				topic: Topics.CREATE_SOCIAL_POST,
			});
			toast.success('Your post has been saved as a draft.');

			try {
				await onPostScheduled(socialMediaPostVm);
				locationStateCreateRequest?.onFinish?.(true);
				if (onPostUpdated?.()) {
					return;
				}
				const locationState: ILocationState<Api.SocialMediaPostViewModel, null> = {
					viewModel: socialMediaPostVm,
				};
				fullscreenModal.history.replace({
					pathname: `${routeMatch.url}`,
					state: locationState,
				});
			} finally {
				setIsSchedulingInProgress(false);
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('CreateSocialPost', error);
			setIsSchedulingInProgress(false);
		},
	});
	const onSaveAsDraftPost = async (newCampaignName?: string, cancel?: boolean) => {
		if (!cancel && newCampaignName) {
			setIsSchedulingInProgress(true);
			context.setCampaignName(newCampaignName);
			const post: Api.ICreateSocialMediaPostRequest = {
				...context.getPostBase(),
				forUserId: context.selectedCreator?.id,
				sendWithCompliance:
					impersonationContext?.account?.preferences?.complianceSettings?.enabled ||
					userSession?.account?.preferences?.complianceSettings?.enabled,
				sendWithComplianceEmail,
				name: newCampaignName,
			};
			createSocialPostMutation.mutate({
				request: post,
				forUserId: post?.forUserId,
				contentCalendarSuggestionId: suggestionId,
				impersonationContext,
			});
		}
		setShowSaveAsModal(false);
	};

	const onPostScheduled = React.useCallback(
		async (postViewModel: Api.SocialMediaPostViewModel, switchToDraftEditor = false) => {
			if (postViewModel) {
				await postViewModel.impersonate(impersonationCtx).load();
				setScheduledPost(postViewModel);
				context.setCampaignName(postViewModel.name);
				context.setHasChanges(true);

				if (
					switchToDraftEditor &&
					!fullscreenModal?.history?.location?.pathname?.includes('/social/edit') &&
					!impersonationContext?.isValid
				) {
					fullscreenModal?.history?.replace({
						pathname: `/social-media/post/edit-post-draft/${postViewModel.id}`,
						state: {
							viewModel: postViewModel,
						},
					});
				}
			}
		},
		[context, fullscreenModal?.history, impersonationContext?.isValid, impersonationCtx, setScheduledPost]
	);

	const onSubmitPost = React.useCallback(
		async (_: React.MouseEvent<HTMLElement>, selectedSendTime?: Moment, ignoreCompliance?: boolean) => {
			if (isSchedulingInProgress) {
				return;
			}
			// The composer already disables the schedule controls over a platform limit, but the confirm
			// lives on a later screen and the draft can change under it. Re-checked here so nothing the
			// API refuses is ever sent, and so the reason reaches the user instead of a generic failure.
			// Saving a draft has no send time and answers to the narrower save rules alone.
			const blockers = selectedSendTime
				? platformCustomization.errors
				: getSaveBlockingErrors(platformCustomization.errors);
			if (blockers.length) {
				setPreflightAction(selectedSendTime ? 'schedule' : 'save');
				setPreflightErrors(blockers);
				return;
			}
			setIsSchedulingInProgress(true);
			const { schedulePost, updatePost } = context;
			try {
				if (postReport || postToEdit || scheduledPost) {
					// RegEd files via the metadata, not an email recipient — never forward the legacy email.
					const complianceEmail =
						isComplianceFlow && !ignoreCompliance && !usesRegEd ? sendWithComplianceEmail : undefined;
					const [, error] = await updatePost(
						scheduledPost,
						selectedSendTime,
						ignoreCompliance,
						complianceEmail,
						usesRegEd
					);
					if (error) {
						logApiError('UpdateSocialMediaPost-Error', error);
						errorMessages.pushApiError(error);
						return;
					}

					if (source) {
						reloadSource();
					}

					if (postToEdit) {
						try {
							await postToEdit.load();
						} catch (err) {
							logApiError('UpdateSocialMediaPost-Error', err);
							errorMessages.pushApiError(err);
							return;
						}
					}

					// The PUT response isn't guaranteed to carry the server-built approval request, so file off a
					// freshly reloaded post.
					let complianceSubmitted = true;
					// Gated on the bypass, not on empty metadata: Post Now submits in the same tick as its
					// state update, and the revise flow seeds metadata anyway.
					// An open round with the internal reviewer stays theirs on save, so there is nothing to file with RegEd.
					const editsInternalRound = holdsOpenInternalRound((postToEdit || scheduledPost)?.approvalRequests);
					if (usesRegEd && !officerFilesRegEd && regEdMetadata && !ignoreCompliance && !editsInternalRound) {
						// Reload with impersonation so the filing keys off the server-built AR — a Draft→Scheduled
						// edit that first turns compliance on builds a fresh AR the pre-update VM doesn't carry.
						const compliancePost = postToEdit || scheduledPost;
						try {
							if (compliancePost) {
								await compliancePost.impersonate(impersonationCtx).load();
							}
						} catch (err) {
							logApiError('ReloadSocialMediaPost-Error', err);
						}
						complianceSubmitted = await submitRegEdCompliance(compliancePost, regEdMetadata);
					}

					if (complianceSubmitted) {
						toast.success(
							!selectedSendTime
								? 'Your draft has been updated.'
								: selectedSendTime.isSame(moment(), 'hour')
									? 'Your post has been successfully sent.'
									: postToEdit?.status === Api.PostStatus.Pending
										? 'Your post has been successfully updated.'
										: 'Your post has been successfully scheduled.'
						);
					}

					if (onPostUpdated?.()) {
						return;
					}

					if (isComplianceFlow && !ignoreCompliance && selectedSendTime) {
						h.replace(`${routeMatch.url}/compliance-complete`);
					} else if (selectedSendTime) {
						const hasCampaignSession = CampaignSessionManager.hasActiveSession();
						const isSendNow = selectedSendTime.diff(moment(), 'minutes') <= 10;
						const isPrimarySendNow = CampaignSessionManager.isPrimaryResourceSendNow('social', isSendNow);
						const scheduledDate = selectedSendTime.toDate();

						if (hasCampaignSession) {
							const socialContext = CampaignSessionManager.createAIContextFromSocialData(postToEdit || scheduledPost);
							const progress = socialContext
								? CampaignSessionManager.updateSessionProgress('social', socialContext, scheduledDate)
								: null;

							// If campaign is complete or it's send now, dismiss modal
							if (progress?.isCampaignComplete || isPrimarySendNow) {
								CampaignSessionManager.clearSession();
								fullscreenModal.dismissModal();
								return;
							}

							handleCampaignSessionNavigation(progress, postToEdit || scheduledPost);
							return; // Exit early to prevent fallthrough
						}

						// Only check these conditions if no campaign session
						if (isPrimarySendNow || isTestimonialMode) {
							CampaignSessionManager.clearSession();
							fullscreenModal.dismissModal();
						} else {
							fullscreenModal.history.replace({
								pathname: `${routeMatch.url}/schedule-complete`,
								state: { socialPost: postToEdit || scheduledPost },
							});
						}
					} else {
						const locationState: ILocationState<Api.SocialMediaPostViewModel, null> = {
							viewModel: postToEdit,
						};
						fullscreenModal.history.replace({
							pathname: `${routeMatch.url}`,
							state: locationState,
						});
					}
					postNotification({
						info: postToEdit,
						topic: Topics.EDIT_SOCIAL_POST,
					});
				} else {
					const hadCampaignSession = CampaignSessionManager.hasActiveSession();
					const [postViewModel, error] = await schedulePost(
						selectedSendTime,
						suggestionId,
						impersonationCtx,
						ignoreCompliance,
						// RegEd files via the metadata, not an email recipient — never forward the legacy email.
						usesRegEd ? undefined : sendWithComplianceEmail,
						usesRegEd
					);
					if (error) {
						logApiError('CreateSocialMediaPost-Error', error);
						errorMessages.pushApiError(error);
						return;
					}

					const complianceSubmitted =
						usesRegEd && !officerFilesRegEd && regEdMetadata && !ignoreCompliance
							? await submitRegEdCompliance(postViewModel, regEdMetadata)
							: true;
					if (complianceSubmitted) {
						toast.success(
							selectedSendTime.isSame(moment(), 'hour')
								? 'Your post has been successfully sent.'
								: 'Your post has been successfully scheduled.'
						);
					}

					// Update campaign session - use startSession to properly handle primary vs secondary resource logic
					const aiContext = CampaignSessionManager.createAIContextFromSocialData(postViewModel);
					let sessionProgress: SessionProgressUpdate | null = null;
					const scheduledDate = postViewModel.dueMoment ? postViewModel.dueMoment.toDate() : selectedSendTime?.toDate();

					if (aiContext) {
						if (hadCampaignSession) {
							sessionProgress = CampaignSessionManager.updateSessionProgress('social', aiContext, scheduledDate);
						} else {
							CampaignSessionManager.startSession('social', aiContext, scheduledDate);
						}
					}

					await onPostScheduled(postViewModel);
					locationStateCreateRequest?.onFinish?.(true);

					const postUpdatedResult = onPostUpdated?.();

					if (postUpdatedResult) {
						return;
					}

					if (isComplianceFlow && !ignoreCompliance && selectedSendTime) {
						h.replace(`${routeMatch.url}/compliance-complete`);
					} else {
						const isSendNow = selectedSendTime.diff(moment(), 'minutes') <= 10;
						const isPrimarySendNow = CampaignSessionManager.isPrimaryResourceSendNow('social', isSendNow);

						// Check if there's currently an active session after session updates
						const hasActiveCampaignSession = CampaignSessionManager.hasActiveSession();

						if (hasActiveCampaignSession) {
							// If campaign is complete or it's send now for primary with no prior session, dismiss modal
							if (sessionProgress?.isCampaignComplete || (!hadCampaignSession && isPrimarySendNow)) {
								CampaignSessionManager.clearSession();
								fullscreenModal.dismissModal();
								return;
							}

							handleCampaignSessionNavigation(sessionProgress, postViewModel);
							return; // Exit early
						}

						// Only handle non-session cases
						if (isPrimarySendNow || isTestimonialMode) {
							CampaignSessionManager.clearSession();
							fullscreenModal.dismissModal();
						} else {
							fullscreenModal.history.replace({
								pathname: `${routeMatch.url}/schedule-complete`,
								state: { socialPost: postViewModel },
							});
						}
					}
					postNotification({
						info: postViewModel,
						topic: Topics.CREATE_SOCIAL_POST,
					});
				}
			} finally {
				context.setHasChanges(false);
				setIsSchedulingInProgress(false);
			}
		},
		[
			context,
			errorMessages,
			fullscreenModal,
			h,
			handleCampaignSessionNavigation,
			impersonationCtx,
			isComplianceFlow,
			isSchedulingInProgress,
			isTestimonialMode,
			locationStateCreateRequest,
			logApiError,
			officerFilesRegEd,
			onPostScheduled,
			onPostUpdated,
			platformCustomization.errors,
			postNotification,
			postReport,
			postToEdit,
			regEdMetadata,
			reloadSource,
			routeMatch.url,
			scheduledPost,
			sendWithComplianceEmail,
			source,
			submitRegEdCompliance,
			suggestionId,
			usesRegEd,
		]
	);
	const onViewCitationsClicked = async () => {
		setShowingCitations(true);
	};

	const onUserOptionSelected = async (option: ISelectOption<UserViewModel<IUser>>, user?: UserViewModel<IUser>) => {
		initialSelectedUserOptionRef.current = option;
		if (impersonationCtx && option?.dataContext) {
			impersonationCtx.user = { id: user?.id };
			user.impersonate(impersonationCtx);
		}
		if (
			(!option.dataContext.socialMediaConnectedAccounts ||
				option.dataContext.socialMediaConnectedAccounts?.length === 0) &&
			user
		) {
			try {
				await user.load();
				context.setSelectedCreator(user.toJs());
			} catch (err) {
				errorMessages.pushApiError(err);
				logApiError('LoadUser-Error', err);
			}
		} else if (
			(!option.dataContext.socialMediaConnectedAccounts ||
				option.dataContext.socialMediaConnectedAccounts?.length === 0) &&
			!user
		) {
			try {
				await option.dataContext.load();
				context.setSelectedCreator(option.dataContext.toJs());
			} catch (err) {
				errorMessages.pushApiError(err);
				logApiError('LoadUser-Error', err);
			}
		} else {
			context.setSelectedCreator(option.dataContext);
		}
		context.setHasChanges(true);
		const connections =
			option.dataContext.socialMediaConnectedAccounts?.filter(
				x =>
					(x.state === Api.SocialMediaConnectionState.Connected ||
						x.state === Api.SocialMediaConnectionState.Disconnected) &&
					!!x.postTargetId
			) ||
			user?.socialMediaConnectedAccounts?.filter(
				x =>
					(x.state === Api.SocialMediaConnectionState.Connected ||
						x.state === Api.SocialMediaConnectionState.Disconnected) &&
					!!x.postTargetId
			) ||
			[];
		setSocialMediaConnections(
			connections?.filter(x => x.state === Api.SocialMediaConnectionState.Connected && !!x.postTargetId)
		);

		// Update below to account for locationStateCreateRequest
		context.setPostTargets(
			connections
				?.filter(
					x =>
						x.state === Api.SocialMediaConnectionState.Connected &&
						x?.postTargetId &&
						(!locationStateCreateRequest ||
							!locationStateCreateRequest?.targets?.length ||
							locationStateCreateRequest?.targets?.includes(x.type))
				)
				.map(y => ({ pageId: y.postTargetId, provider: y.type, userId: y.userId })) || []
		);
	};

	const onAIContentGenerationAssistantButtonClicked = () => {
		setShowingAIAssistant(true);
	};

	const IGNORE_EXPIRED_DATE = [
		Api.PostStatus.Draft,
		Api.PostStatus.Succeeded,
		Api.PostStatus.PartiallySucceeded,
		Api.PostStatus.Started,
		Api.PostStatus.Cancelled,
		Api.PostStatus.Failed,
	];

	const expirationDateInEffect =
		context?.template?.schedule?.expirationDate && !IGNORE_EXPIRED_DATE.includes(source?.status);

	const handleOnRequestClose = (suppress?: boolean, cancel?: boolean) => {
		if (suppress) {
			suppressDuplicateTemplateWarning(templateId);
		}
		if (cancel) {
			setShowDuplicateWarning(false);
			fullscreenModal?.dismissModal();
			return;
		}
		setShowDuplicateWarning(false);
	};

	const onEditNameCancelButtonClicked = () => {
		setIsEditingName(false);
	};

	const onEditNameValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setEditableCampaignName(value);
	};

	const onEditNameSaveButtonClicked = () => {
		context.setCampaignName(editableCampaignName);
		context.setHasChanges(true);
		setIsEditingName(false);
	};

	const onEditNameButtonClicked = () => {
		setIsEditingName(true);
	};

	const [isRevising, setIsRevising] = React.useState(false);
	const [reviseDueDate, setReviseDueDate] = React.useState<Date | null>(null);
	const regEdSocialRevise = useRegEdSocialPostReviseMutation({
		onError: error => errorMessages.pushApiError(error),
	});
	const regEdSocialRegeneratePdf = useRegEdSocialPostRegenerateCompliancePdfMutation({
		onError: error => errorMessages.pushApiError(error),
	});

	const revisePost = scheduledPost || postToEdit;
	const {
		finishSubmission,
		isFinishingSubmission,
		isRetryingRevision,
		onRetryRevisionClicked,
		revisionOwed,
		runRevisionFiling,
	} = useRegEdRevisionFiling({
		getImpersonationContext: () => Api.ImpersonationBroker.withForUser(impersonationCtx, context.selectedCreator?.id),
		logApiError,
		// The post arrives as a prop and the viewmodel exposes no way to apply the revised copy, so the
		// editor would keep rendering the layout the revision has already left.
		onRevisionFiled: () => {
			postNotification({ info: revisePost, topic: Topics.EDIT_SOCIAL_POST });
			parentModal?.onRequestClose?.();
		},
		parentId: revisePost?.id,
		parentType: RegEdComplianceContentType.SocialMediaPost,
		regenerateCompliancePdf: (impersonation?: Api.IImpersonationContext) =>
			regEdSocialRegeneratePdf.mutateAsync({ impersonationContext: impersonation, postId: revisePost.id }),
		userSession,
	});

	/**
	 * Four calls whose order the API does not enforce: save the body (`revise` snapshots it), reopen
	 * the round, repoint the RegEd submission onto that round, then render the PDF — which is what
	 * files. `RegEdCompliancePdfGeneratedHandler` resolves the submission by the new round's id and
	 * gives up if it is missing, so skipping the repoint submits nothing and says so to no one.
	 */
	const handleReviseCompliance = async (submission: IRegEdSubmissionView) => {
		const post = scheduledPost || postToEdit;
		if (!post) {
			return;
		}
		setIsRevising(true);
		try {
			const [, saveError] = await context.updatePost(post);
			if (saveError) {
				errorMessages.pushApiError(saveError);
				toast.error('We could not save your changes, so the post was not sent back for review.');
				return;
			}
			const revised = await regEdSocialRevise.mutateAsync({
				impersonationContext: Api.ImpersonationBroker.withForUser(impersonationCtx, context.selectedCreator?.id),
				postId: post.id,
			});
			// After revise, not with the save: UpdateAsync gives a dueDate change its own side effects —
			// on a Cancelled post it raises an approval round of its own, which would collide with the one
			// revise just raised. Revise leaves the post Pending, and that whole block is skipped for a
			// Pending post, so the date applies and nothing else does.
			// Only when it actually moved: the dialog seeds this from the post's own date, so treating any
			// value as a change would fire a second save on every revision.
			const dueDateChanged = reviseDueDate && moment(reviseDueDate).valueOf() !== moment(post.dueDate).valueOf();
			if (dueDateChanged) {
				const [, dateError] = await context.updatePost(post, moment(reviseDueDate));
				if (dateError) {
					// Filing anyway would submit against the date the advisor was just told cannot be
					// approved, and that 400 surfaces to the reviewer rather than to them.
					errorMessages.pushApiError(dateError);
					toast.error('We could not update the posting date, so the post was not sent back for review.');
					return;
				}
				// Cleared once applied: only the cancelled branch's dialog offers a date, so a value left
				// here could otherwise reschedule a later revision started from the branch that does not.
				setReviseDueDate(null);
			}
			const raisedRound = findRaisedRegEdRound(revised?.approvalRequests);
			if (!raisedRound?.id) {
				// Nothing to file against, and a retry would replay the same empty call forever.
				logApiError('RegEdSocialRevise-NoRaisedRound');
				toast.error(
					'The review was reopened, but we could not tell which round to file against. Reopen the post and try again.'
				);
				return;
			}
			await runRevisionFiling(raisedRound.id, submission, true);
		} catch (error) {
			logApiError('RegEdSocialRevise-Error', Api.asApiError(error));
		} finally {
			setIsRevising(false);
		}
	};

	const renderDefaultComposerContext = () => {
		// Nothing for the overlay toggle to do without a logo, or without an image to draw it on (the publisher
		// skips videos), and a cancelled or failed post is past the point where changing its media would mean
		// anything.
		const account = impersonationCtx?.account || userSession.account;
		const showMediaOverlayOptions =
			!!account?.features?.socialMedia?.enabled &&
			!!account?.preferences?.logo?.url &&
			platformCustomization.publishedAttachments.some(x => !x.mimeType?.toLowerCase().startsWith('video/')) &&
			!platformCustomization.publishedAttachments.some(x => x.hasLogoOverlay) &&
			getStatusGroupOptions(postToEdit || scheduledPost) !== SocialMediaPostStatusDetails.CancelledOrFailed;

		return (
			<DefaultComposerContext
				context={context}
				impersonationCtx={impersonationCtx}
				expirationDateInEffect={expirationDateInEffect}
				platformCustomization={platformCustomization}
				onRenderStatusSpecificDetails={({ complianceFormOpen, onComplianceFormOpenChange }) => {
					return (
						<StatusSpecificDetails
							complianceFormOpen={complianceFormOpen}
							onComplianceFormOpenChange={onComplianceFormOpenChange}
							postToEdit={postToEdit}
							scheduledPost={scheduledPost}
							context={context}
							isComplianceFlow={isComplianceFlow}
							sendWithComplianceEmail={sendWithComplianceEmail}
							setSendWithComplianceEmail={setSendWithComplianceEmail}
							isSchedulingInProgress={isSchedulingInProgress}
							impersonationCtx={impersonationCtx}
							handleScheduleForLater={handleScheduleForLater}
							handleScheduleWithoutCompliance={handleScheduleWithoutCompliance}
							handlePostNow={handlePostNow}
							checkComplianceEmail={checkComplianceEmail}
							lastUsedComplianceRecipientEmail={lastUsedComplianceRecipientEmail}
							handleChangeDate={handleChangeDate}
							onViewCitationsClicked={onViewCitationsClicked}
							source={source}
							usesRegEd={usesRegEd}
							officerFilesRegEd={officerFilesRegEd}
							officerReviewerMissing={officerReviewerMissing}
							regEdProbeLoading={regEdProbeLoading}
							onScheduleRegEdCompliance={onScheduleRegEdCompliance}
							regEdMetadata={regEdMetadata}
							regEdRevisesApprovalRequestId={locationStateCreateRequest?.regEdRevise?.revisesApprovalRequestId}
							onReviseCompliance={handleReviseCompliance}
							onReviseDueDateChange={setReviseDueDate}
							onRetryRevisionClicked={onRetryRevisionClicked}
							onFinishRegEdSubmission={finishSubmission}
							isFinishingRegEdSubmission={isFinishingSubmission}
							revisionOwed={revisionOwed ? { needsFiling: revisionOwed.needsFiling } : null}
							isRetryingRevision={isRetryingRevision || regEdSocialRegeneratePdf.isPending}
							isRevising={isRevising}
							hasPlatformValidationErrors={platformCustomization.hasBlockingErrors}
							isBrandingInProgress={branding.isBranding}
							isSplitByPlatform={platformCustomization.isSplit}
						/>
					);
				}}
				overlayLogo={context.overlayLogo}
				hideWhiteBackground={context.hideWhiteBackground}
				logoSize={context.logoSize}
				showMediaOverlayOptions={showMediaOverlayOptions}
				onOverlayLogoChange={onOverlayLogoChange}
			/>
		);
	};
	const postContentState = React.useMemo(
		() => createContentStateWithHtmlStringValue(context.postContent).getRawRichTextContent(),
		[context.postContent]
	);
	// The versions are saved beside the base body, the same pair a post carries, so reusing the
	// template reproduces the post instead of making the author rewrite each network's copy. A template
	// has no targets, so it keeps every version the draft holds: filtering by this post's targets would
	// erase the ones for networks this account happens not to use. The API refuses a blank version, and
	// one for an untargeted platform is nothing the author can see to fix, so that one is left out.
	const templatePlatformCustomizations = () => {
		if (!context.customizeByPlatform) {
			return [];
		}
		const targetedPlatforms = getTargetedPlatforms(context.postTargets);
		const keptPlatforms = Api.SupportedSocialMediaTypes.filter(platform => {
			const version = findPlatformCustomization(context.platformCustomizations, platform);
			return !!version && (targetedPlatforms.includes(platform) || !!version.content.trim());
		});
		return toApiPlatformCustomizations(context.platformCustomizations, keptPlatforms, context.facebookMentions);
	};

	const onSaveAsTemplateClicked = () => {
		const newTemplate: Api.ISocialMediaPostTemplate = {
			...context?.template,
			attachments: context.postImages,
			content: postContentState,
			name: context.campaignName,
			platformCustomizations: templatePlatformCustomizations(),
			scope: context?.template?.scope,
			templateType: Api.TemplateType.SocialMediaPost,
		};
		delete newTemplate.id;
		setTemplateToBeSaved(newTemplate);
	};

	const onForkedTemplate = () => {
		setTemplateToBeSaved(null);
		invalidateAllTemplateCategorySocialQueries();

		// Don't redirect if we're in admin context (admin URLs contain '/admin' or start with admin routes)
		const isAdminContext = window.location.pathname.includes('/admin') || window.location.hash.includes('/accounts/');

		if (!isAdminContext) {
			h.push(`/campaigns/social?category=${KnownCategories.MyTemplates}`);
		}
	};
	const updateTemplateMutation = useUpdateTemplateMutation({
		onSuccess: template => {
			toast.success('Template saved successfully');
			context.setTemplate(template);
			invalidateAllTemplateCategorySocialQueries();
			setShowingUpdateConfirmation(postReport != null || postToEdit != null || scheduledPost != null);
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ErrorSavingSocialTemplate', error);
		},
	});
	const handleSave = () => {
		const template: Api.ISocialMediaPostTemplate = {
			...context?.template,
			attachments: context.postImages,
			content: postContentState,
			name: context.campaignName,
			platformCustomizations: templatePlatformCustomizations(),
			scope: context?.template?.scope,
			templateType: Api.TemplateType.SocialMediaPost,
		};
		updateTemplateMutation.mutate({ template });
	};

	const canSaveTemplate =
		(context.template?.scope === Api.TemplateScope.User || context.template?.scope === Api.TemplateScope.Account) &&
		(context.template?.creator?.id === userSession.user.id || userSession.userRole.includes('admin'));

	const onRenderComposer = () => {
		if (!isLoaded || !userIsLoaded) {
			return <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />;
		}
		const isUsingAccountImpersonation = !!impersonationCtx;
		const canSendOnBehalf = shouldShowSendOnBehalfUI({
			isUsingAccountImpersonation,
			userRole: userSession?.userRole,
			sendOnBehalfEnabled: userSession?.account?.preferences?.sendOnBehalfEnabled,
		});

		const newPost = !source && !scheduledPost;
		const heading = !templateId && !source && !context.campaignName ? 'New Post' : context.campaignName;
		let socialMediaTableType: SocialMediaPlatformTableType = SocialMediaPlatformTableType.Reporting;
		socialMediaTableType = (!source || (source.canEdit && !impersonationCtx)) && SocialMediaPlatformTableType.Mutable;
		socialMediaTableType =
			((source && impersonationCtx) || (source && !source?.canEdit)) && SocialMediaPlatformTableType.ReadOnly;

		let socialUserOptions: ISelectOption<string | UserViewModel>[] = [];
		if (usersWithSocial?.data?.length > 0) {
			socialUserOptions = usersWithSocial.data
				.filter(x => x.id !== userSession?.user?.id)
				.filter(x => x.id !== context.selectedCreator?.id)
				.map<ISelectOption>(x => {
					return {
						dataContext: x,
						id: `option-${x.id}-social`,
						text: `${x.firstName} ${x.lastName}`,
					};
				});
		}
		return (
			<>
				<div className={css(styleSheet.composerContainer)}>
					<div className={css(styleSheet.composerContainerHeader)}>
						<div className={css(styleSheet.nameContainer, baseStyleSheet.truncateText)}>
							<div className={css(styleSheet.headingEditor)}>
								{!isEditingName ? (
									<div className={css(styleSheet.heading)}>{heading}</div>
								) : (
									<InlineValueEditor
										autoComplete='off'
										autoFocus={true}
										className={`inline-value-editor ${css(styleSheet.headingEditorInput)}`}
										id='post-name-editor-input'
										onCancelButtonClicked={onEditNameCancelButtonClicked}
										onChange={onEditNameValueChanged}
										onSaveButtonClicked={onEditNameSaveButtonClicked}
										value={editableCampaignName || context.campaignName || 'New Post'}
									/>
								)}
								{!isEditingName && !readOnly ? (
									<button disabled={false} onClick={onEditNameButtonClicked}>
										<EditItemPenIcon className={css(styleSheet.editIcon)} />
									</button>
								) : null}
								{getTemplateApprovalStatus(context?.template?.templateReview)}
							</div>
							<div className={css(styleSheet.buttonContainer)}>
								{!impersonationCtx || source ? <CopyPreviewLink anonymousAccessLink={previewLink} /> : null}

								{context?.template?.citation?.length > 0 ? (
									<button
										className={css(baseStyleSheet.ctaButtonReverseSmall, styleSheet.citationButton)}
										onClick={onViewCitationsClicked}
									>
										<span>View Citations</span>
									</button>
								) : null}
							</div>
						</div>
						{newPost && context?.template?.summary?.length > 0 ? (
							<div className={css(styleSheet.summary)}>
								<LightBulbIcon className={css(styleSheet.lightBulb)} fill='#00aae8' />
								<div>{context?.template?.summary}</div>
							</div>
						) : null}
						{newPost && canSendOnBehalf ? (
							<div className={css(styleSheet.postFrom)}>
								<span>Post From:</span>
								<div className={css(styleSheet.userDropdown)}>
									{impersonationCtx ? (
										<AccountUserSelectBox
											defaultOption={null}
											onOptionSelected={onUserOptionSelected}
											onRenderPlaceholder={() => <span>Select User</span>}
											adminsFilter={x => x.socialMediaConnectedAccounts?.length > 0}
											initialSelectedOption={initialSelectedUserOptionRef.current}
										/>
									) : (
										<UserSelectBox
											defaultOption={null}
											includeSearch={false}
											onOptionSelected={onUserOptionSelected}
											onRenderPlaceholder={() => <span>Select User</span>}
											initialSelectedOption={initialSelectedUserOptionRef.current}
											additionalPinnedOptions={socialUserOptions}
										/>
									)}
								</div>
								<TinyPopover
									anchor={
										<figure className={css(baseStyleSheet.flex, baseStyleSheet.itemsCenter)}>
											<InfoIcon className={css(styleSheet.infoIcon)} fillColor={brandPrimaryHover} />
										</figure>
									}
									className={css(styleSheet.infoPopover)}
									dismissOnOutsideAction={true}
									toggleOnHover={true}
									autoCloseOtherPopoversOnHover={true}
									type={PopoverType.background}
									placement={['right', 'top', 'bottom']}
								>
									<p className={css(styleSheet.popoverContent)}>Users with connected social media accounts.</p>
								</TinyPopover>
							</div>
						) : null}
					</div>
					{!readOnly ? (
						<SocialMediaNavigation
							onAIContentGenerationAssistantButtonClicked={onAIContentGenerationAssistantButtonClicked}
							canSaveTemplate={canSaveTemplate}
							updateTemplateMutation={updateTemplateMutation}
							handleSave={handleSave}
							onSaveAsTemplateClicked={onSaveAsTemplateClicked}
							newPost={newPost}
							impersonationCtx={impersonationCtx}
							context={context}
							onSaveAsButtonClicked={onSaveAsButtonClicked}
							source={source}
							onShowCancelConfirmation={onShowCancelConfirmation}
							onShowUpdateConfirmation={onShowUpdateConfirmation}
							hasPlatformValidationErrors={platformCustomization.hasBlockingErrors}
							hasPlatformSaveErrors={platformCustomization.hasSaveBlockingErrors}
							isBrandingInProgress={branding.isBranding}
							isSplitByPlatform={platformCustomization.isSplit}
						/>
					) : null}
					<UnsavedChangesWarning
						hasUnsavedChanges={!!context?.hasChanges}
						message='You have unsaved changes to this post.'
					/>
					<div className={css(styleSheet.composerContainerBody)}>
						{locationStateCreateRequest?.sendFromUser?.id === userSession.user.id || context?.selectedCreator ? (
							<div>
								<div className={css(styleSheet.whereToShare, styleSheet.whereToShareAdmin)}>
									<p>Where to Share</p>
								</div>
								{socialMediaConnections?.length ? (
									<SocialMediaPlatformTable
										connections={socialMediaConnections}
										impersonationContext={impersonationCtx}
										onOrphanedTargetRemoved={onOrphanedTargetRemoved}
										onSelectionChanged={onSelectedConnectionsChanged}
										source={source}
										type={socialMediaTableType}
									/>
								) : (
									<div className={css(styleSheet.noAccountsWarning)}>
										Selected user is currently not linked to any social media accounts.
									</div>
								)}
							</div>
						) : null}
						<SocialMediaPostContentEditor
							branding={branding.getBrandingState}
							allowPlatformCustomization
							imagesLabel='Add Media'
							messageLabel='Content'
							impersonationContext={impersonationContext}
							onPostScheduled={vm => onPostScheduled(vm, true)}
							post={postToEdit || scheduledPost}
							readOnly={readOnly && scheduledPost?.status !== PostStatus.Draft}
						/>
					</div>
				</div>
				{!showingAIAssistant ? (
					<div className={css(styleSheet.contextContainer)}>
						{hasPublishedContent(context) || platformCustomization.publishedAttachments.length > 0 ? (
							renderDefaultComposerContext()
						) : (
							<div className={css(styleSheet.graphicContainer)}>
								<div className={css(styleSheet.sloganContainer)}>Let&apos;s create a post</div>
								<SocialMediaPostGraphic />
							</div>
						)}
					</div>
				) : null}
				<div className={css(styleSheet.assistantContainer)}>
					{/* We handle conitional rendering within the component, so we can manage the chat session. Don't wrap this in a conditional. */}
					<UniversalAssistantChatWithinComposer
						isOpen={showingAIAssistant}
						onUseSocialContent={(content, aiReference) => {
							context.loadContent(getSocialMediaPlainTextContentFromRawRichContent(content));
							context.setAIReference(aiReference);
						}}
						socialContext={{
							content: postContentState,
						}}
						emailContext={sourceType === 'email' && sourceContent ? (sourceContent as Api.EmailAIContext) : undefined}
						blogContext={sourceType === 'blog' && sourceContent ? (sourceContent as Api.BlogAIContext) : undefined}
						impersonationContext={impersonationContext?.toJs()}
						initialGeneration={initialGeneration}
						onClose={() => setShowingAIAssistant(false)}
					/>
				</div>
			</>
		);
	};

	const onModalCloseWithUnsavedChangesWarning = (e: React.MouseEvent<HTMLElement>) => {
		if (context?.hasChanges) {
			onShowCancelUpdateConfirmation(e);
			return;
		}
		onRequestClose();
	};

	const onBackButtonClicked = (e: React.MouseEvent<HTMLElement>) => {
		if (location.pathname === '/social-media/post/create') {
			e.preventDefault();
			onModalCloseWithUnsavedChangesWarning(e);
			return;
		}
	};
	// send-options and set-social-schedule mount their own header; portaling this one too doubles the chrome.
	const subPageOwnsHeader =
		location.pathname.startsWith(`${routeMatch.url}/send-options`) ||
		location.pathname.startsWith(`${routeMatch.url}/set-social-schedule`);
	return (
		<div className={`${css(styleSheet.container, ...styles)} edit-social-media-post ${className || ''}`}>
			{!subPageOwnsHeader ? (
				<MultiContainerHeader
					fullscreenHeader={
						!postReport && !postToEdit && !scheduledPost
							? 'Create Post'
							: readOnly || (scheduledPost && !postReport)
								? 'Viewing Post'
								: 'Edit Post'
					}
					onFullscreenRequestClose={onModalCloseWithUnsavedChangesWarning}
					onFullscreenRequestBack={onBackButtonClicked}
				/>
			) : null}
			<SchedulePreflightDialog
				action={preflightAction}
				errors={preflightErrors}
				isOpen={!!preflightErrors.length}
				onRequestClose={() => setPreflightErrors([])}
			/>
			<DuplicateWarningDialog
				isOpen={showDuplicateWarning}
				onRequestClose={handleOnRequestClose}
				title='Social Media Post Duplicate'
				type={CampaignType.Social}
				duplicateType={duplicateType}
				lastUsedDate={
					duplicateType === EDuplicateType.Account
						? context?.template?.lastUsedByDate?.account
						: context?.template?.lastUsedByDate?.currentUser
				}
			/>
			<EditSocialMediaPostContext.Provider value={context}>
				<Switch>
					<PrivateRoute exact={false} path={`${routeMatch.url}/set-social-schedule`} userSession={userSession}>
						<SocialMediaApprovalSetSchedule
							postVm={scheduledPost}
							sendOnBehalf={
								(context?.selectedCreator?.id !== userSession.user.id && !impersonationContext?.isValid) ||
								impersonationContext?.isValid
							}
							expirationDate={
								scheduledPost?.status !== Api.PostStatus.Draft ? context?.template?.schedule?.expirationDate : null
							}
							hasRegEdFiling={!!regEdMetadata}
							minimumStartDate={regEdMetadata?.earliestSendDate}
							regEdReviewNotice={regEdMetadata?.reviewNotice}
							scheduleCtaText={scheduledPost?.status === PostStatus.Pending ? 'Okay' : 'Update Schedule'}
						/>
					</PrivateRoute>
					<PrivateRoute path={`${routeMatch.url}/send-options`} userSession={userSession}>
						<EditSocialMediaSendOptions
							selectedUser={context?.selectedCreator}
							sendOnBehalf={
								(context?.selectedCreator?.id !== userSession.user.id && !impersonationContext?.isValid) ||
								impersonationContext?.isValid
							}
							ignoreCompliance={bypassCompliance}
							initialStartDate={moment(
								postReport?.dueDate || postToEdit?.dueDate || locationStateCreateRequest?.schedule?.startDate
							)}
							onSubmitPost={onSubmitPost}
							className={css(styleSheet.sendOptions)}
							expirationDate={
								postToEdit?.status !== Api.PostStatus.Draft ? context?.template?.schedule?.expirationDate : null
							}
							hasRegEdFiling={!!regEdMetadata}
							minimumStartDate={regEdMetadata?.earliestSendDate}
							regEdReviewNotice={regEdMetadata?.reviewNotice}
							templateId={routeMatch.params.templateId}
							isSchedulingInProgress={isSchedulingInProgress}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelConfirmation,
								onRequestClose: onCancelConfirmationRequestClose,
							}}
							options={CancelConfirmationOptions}
							title={`Are you sure you want to ${
								source?.status === Api.PostStatus.Draft ? 'delete' : 'cancel'
							} this social media ${source?.status === Api.PostStatus.Draft ? 'draft' : 'post'}?`}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelUpdateConfirmation,
								onRequestClose: onCancelUpdateConfirmationRequestClose,
							}}
							options={CancelUpdateConfirmationOptions}
							title='Are you sure you want to cancel these updates?'
						/>
					</PrivateRoute>
					<PrivateRoute exact={true} path={`${routeMatch.url}/compliance-complete`} userSession={userSession}>
						<ComplianceConfirmation onDismiss={fullscreenModal?.dismissModal} styles={[styleSheet.childRoute]} />
					</PrivateRoute>
					<PrivateRoute userSession={userSession} path={`${routeMatch.url}/schedule-complete`}>
						{userSession?.account?.features?.contentGeneration?.enabled ? (
							<AIScheduleSuccess
								resourceType='social'
								socialPost={scheduledPost}
								styles={[styleSheet.postCompleteContainer]}
								hideHeader={true}
							/>
						) : (
							<SocialMediaPostScheduleComplete post={scheduledPost} onClose={() => fullscreenModal.dismissModal()} />
						)}
					</PrivateRoute>
					<PrivateRoute userSession={userSession}>
						{onRenderComposer()}
						<CustomizationRequiredDialog
							contentKind='post'
							isOpen={!!customizationMarkers?.length}
							markers={customizationMarkers ?? []}
							onRequestClose={onCustomizationRequiredDialogRequestClose}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelConfirmation,
								onRequestClose: onCancelConfirmationRequestClose,
							}}
							options={CancelConfirmationOptions}
							title={`Are you sure you want to ${
								source?.status === Api.PostStatus.Draft ? 'delete' : 'cancel'
							} this social media ${source?.status === Api.PostStatus.Draft ? 'draft' : 'post'}?`}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingCancelUpdateConfirmation,
								onRequestClose: onCancelUpdateConfirmationRequestClose,
							}}
							options={CancelUpdateConfirmationOptions}
							title='Are you sure you want to cancel these updates?'
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: showingUpdateConfirmation,
								onRequestClose: onUpdateConfirmationRequestClose,
							}}
							options={UpdateConfirmationOptions}
							title='Are you sure you want to update this social media post?'
						/>
						{showSaveAsModal ? (
							<TextInputModal
								cta='Save'
								modalProps={{
									isOpen: showSaveAsModal,
									onRequestClose: onSaveAsDraftPost,
								}}
								placeholderText='Name'
								title='Save as a Draft'
								initialValue={context.campaignName}
							/>
						) : null}
						<Modal
							isOpen={Boolean(templateToBeSaved)}
							onRequestClose={() => setTemplateToBeSaved(null)}
							className={css(styleSheet.saveTemplateModal)}
							useDefaultHeader
							shouldCloseOnOverlayClick={false}
						>
							{templateToBeSaved ? (
								<SaveTemplate
									canImpersonateOnTemplateSave
									template={templateToBeSaved}
									onCancel={() => setTemplateToBeSaved(null)}
									onSuccess={onForkedTemplate}
									enableKeywords
								/>
							) : null}
						</Modal>
						{showingCitations ? (
							<div>
								<ViewCitationsModal modalProps={{ isOpen: showingCitations, onRequestClose: onCitationRequestClose }}>
									{context?.template?.citation}
								</ViewCitationsModal>
							</div>
						) : null}
					</PrivateRoute>
				</Switch>
			</EditSocialMediaPostContext.Provider>
		</div>
	);
});

export const EditSocialMediaPostDesktop = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(EditSocialMediaPostBase);

export const EditSocialMediaPost = (props: IProps) => {
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });

	if (isMobile) {
		return <MobileEditSocialMediaPostContainer {...props} />;
	}
	return <EditSocialMediaPostDesktop {...props} />;
};
